import dcasLogo from "../../assets/images/logo/dcas_logo.png";
import hamburgerBtn from "../../assets/images/header/top_menu_hamburger_btn.svg";
import style from "../../assets/scss/common/header.module.scss";
import React, {useCallback, useEffect, useMemo, useRef, useState} from "react";
import {DrawerMenu} from "./DrawerMenu";
import {Link, useNavigate} from "react-router-dom";
import partnershipIcon from "../../assets/images/header/partnership_icon.svg";
import {
  useAuth,
  useLanguage,
  useMainScrolling,
  useModal,
  usePage,
  useScrollingEvent,
  useUserAgent,
} from "../../hooks/hooks";
import {useTranslation} from "react-i18next";
import {useLocationHistory} from "../../hooks/location/useLocationHistory";
import {LoginBar} from "./LoginBar";
import {useUserInfoGetter} from "./hooks/useUserInfoGetter";
import {GoogleAnalytics} from "./GoogleAnalytics";

export enum MainArticle {
  NONE = "NONE",
  ABOUT_DCAS = "D-CAS란",
  EXPECTATION = "기대효과",
  COMPETITIVENESS = "경쟁력",
  ARTICLE4 = "ARTICLE4",
  ARTICLE5 = "ARTICLE5",
}

export function Header() {
  const { t } = useTranslation(["newHeader", "loginBar"]);
  const navigate = useNavigate();
  const { isDesktop, isMobile } = useUserAgent();
  const { isWithAppPage, isPartnerCompanyPage, isPartnershipPage,  isMainPage } = usePage();
  const [showDrawer, setShowDrawer] = useState(false);

  const { isKorean } = useLanguage();
  const isBannerVisible = isDesktop && isKorean && false; // 어워드 배너 없기때문에 false처리
  const {
    setHeaderHeight,
    scrollingSection,
    setScrollingSection,
    setSelectedSection,
    getArticleElementOffsetTop,
    article1Element,
    article2Element,
    article3Element,
    article4Element,
    article5Element,
  } = useMainScrolling();

  const isProdStatus = process.env.REACT_APP_BASE_URL === "https://service.feel-good.io/";
  const { openConfirmModal, closeModal } = useModal();
  const auth = useAuth();
  const { language, isNotKorean, getLanguageStyle } = useLanguage();
  const { scrollY } = useScrollingEvent();
  const { removeUserInfo } = useUserInfoGetter();

  const onClickHamburgerBtn = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      setShowDrawer(true);
    },
    [setShowDrawer]
  );
  const closeDrawer = useCallback(() => setShowDrawer(false), [setShowDrawer]);
  const { pushHistory } = useLocationHistory({ callbackOnBack: () => closeDrawer() });
  const onClickLogo = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      window.scrollTo(0, 0);
      if (isMainPage) {
        window.location.reload();
      } else {
        navigate("/");
      }
    },
    [isMainPage, navigate]
  );

  const headerRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (showDrawer) {
      pushHistory();
    }
    // eslint-disable-next-line
  }, [showDrawer]);

  useEffect(() => {
    if (headerRef.current) {
      const headerRect = headerRef.current.getBoundingClientRect();
      setHeaderHeight(headerRect.height);
    }
    // eslint-disable-next-line
  }, [headerRef.current, setHeaderHeight, language, isWithAppPage]);

  const onClickMainSection = (e: React.MouseEvent, menu: MainArticle) => {
    e.preventDefault();
    setSelectedSection(menu);
    const offsetTop = getArticleElementOffsetTop(menu);
    if (offsetTop !== null) {
      window.scrollBy({ top: offsetTop - scrollY, left: 0, behavior: "smooth" });
    }
  };

  const confirmAndSignOut = () => {
    openConfirmModal(
      {
        title: t("loginBar:modal.logout.title"),
        desc: t("loginBar:modal.logout.confirm"),
      },
      () =>
        auth.signOut(() => {
          closeModal();
          removeUserInfo();
          navigate("/");
        })
    );
  };

  const activeMenuBaseLine = useMemo(() => window.innerHeight / 3, []);

  useEffect(() => {
    if (!isMainPage) {
      setScrollingSection(MainArticle.NONE);
      return;
    }

    const basedScrollY = scrollY + activeMenuBaseLine;

    if (article1Element.current && article2Element.current && article3Element.current && article5Element.current) {
      // eslint-disable-next-line
      const article1OffsetTop = article1Element.current.offsetTop;
      const article2OffsetTop = article2Element.current.offsetTop;
      const article3OffsetTop = article3Element.current.offsetTop;
      const article5OffsetTop = article5Element.current.offsetTop;

      switch (true) {
        // scrolling section - none
        case basedScrollY < article2OffsetTop:
          setScrollingSection(MainArticle.NONE);
          break;
        // scrolling section - d-cas란
        case basedScrollY < article5OffsetTop:
          setScrollingSection(MainArticle.ABOUT_DCAS);
          break;
        // scrolling section - 기대효과
        case basedScrollY < article3OffsetTop:
          setScrollingSection(MainArticle.EXPECTATION);
          break;
        // scrolling section - 경쟁력
        case basedScrollY > article3OffsetTop:
          setScrollingSection(MainArticle.COMPETITIVENESS);
          break;
      }
    }
    // eslint-disable-next-line
  }, [
    isMainPage,
    scrollY,
    activeMenuBaseLine,
    article1Element,
    article2Element,
    article3Element,
    article4Element,
    article5Element,
  ]);

  return (
      <header
          className={`${style.header} ${isBannerVisible && style.headerBanner} ${isWithAppPage ? style.hide : ""} 
      `}
          ref={headerRef}
      >
      <LoginBar signOut={confirmAndSignOut} />
      <div className={style.headerBg}>
        <div className={style.logo}>
          <h1 className="blind">{t("heading1")}</h1>
          <Link to="/" onClick={onClickLogo}>
            <img src={dcasLogo} alt={t("logoAlt")} />
          </Link>
        </div>
        <div className={style.menuArea}>
          <ul
            className={`${style.mainTopMenu} ${isMainPage ? "" : style.hide} ${isNotKorean ? style.isNotKorean : ""}`}
          >
            <li
              className={`${style.menuItem} ${style.aboutKpass} ${getLanguageStyle(style)} ${
                scrollingSection === MainArticle.ABOUT_DCAS ? style.active : ""
              }`}
            >
              <button className={style.anchor} onClick={(e) => onClickMainSection(e, MainArticle.EXPECTATION)}>
                <span className={style.normalText}>{t("linkDcas")}</span>
                <span className={`${style.activeText} ${isNotKorean ? style.notKO : ""}`}>{t("linkDcas")}</span>
              </button>
            </li>
            <li
              className={`${style.menuItem} ${style.aboutKpass} ${getLanguageStyle(style)} ${
                scrollingSection === MainArticle.EXPECTATION ? style.active : ""
              }`}
            >
              <button className={style.anchor} onClick={(e) => onClickMainSection(e, MainArticle.ARTICLE5)}>
                <span className={style.normalText}>{t("expectation")}</span>
                <span className={`${style.activeText} ${isNotKorean ? style.notKO : ""}`}>{t("expectation")}</span>
              </button>
            </li>
            <li
              className={`${style.menuItem} ${style.competitiveness} ${getLanguageStyle(style)} ${
                scrollingSection === MainArticle.COMPETITIVENESS ? style.active : ""
              }`}
            >
              <button className={style.anchor} onClick={(e) => onClickMainSection(e, MainArticle.COMPETITIVENESS)}>
                <span className={style.normalText}>{t("linkCompetitiveness")}</span>
                <span className={`${style.activeText} ${isNotKorean ? style.notKO : ""}`}>
                  {t("linkCompetitiveness")}
                </span>
              </button>
            </li>
          </ul>
            <div className={style.buttonWrapper}>
              {!isPartnerCompanyPage && isKorean && (!isMobile || (isMobile && isPartnershipPage)) && (
                  <button className={style.partnerCompanyButton} onClick={() => navigate(`/partnerlist`)}>
                    <div className={style.icon}>
                      <div className={style.defaultIcon}></div>
                      <div className={style.hoverIcon}></div>
                    </div>
                    <span className={style.title}>협력사</span>
                  </button>
              )}
              {!isPartnershipPage && (
                  <button className={style.partnershipButton} onClick={() => navigate(`/partnership`)}>
                    <div className={style.hoverBg}></div>
                    <img src={partnershipIcon} alt={"partnership icon"} />
                    <span className={style.title}>{t("partnership")}</span>
                  </button>
              )}
            </div>
          {/*{canLogin && <div className={style.buttonWrapper}></div>}*/}
          <button className={style.hamburger} onClick={onClickHamburgerBtn}>
            <img src={hamburgerBtn} alt={t("hamburgerButtonAlt")} />
          </button>
        </div>
      </div>
      <DrawerMenu showDrawer={showDrawer} closeDrawer={closeDrawer} signOut={confirmAndSignOut} />
      {isProdStatus && <GoogleAnalytics />}
      {/*<GoogleAnalytics />*/}
    </header>
  );
}
